/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER TOR-NOTSCHALTER (S5a Stufe 3b) — reine Regeln, kein Netz
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Seit Stufe 3b ordnet das Tor beim Ausstellen aus dem Buchungsjournal zu
 * und sperrt bei einer Abweichung mit Geldwirkung (allocationGate.ts). Fuer
 * einen Systemfehler — das Journal laeuft dauerhaft nach, eine Rechte-
 * aenderung macht es unlesbar, der Vergleich selbst ist kaputt — gibt es
 * EINEN globalen Schalter zurueck auf den alten Weg: Zuordnung aus dem
 * Projekt-JSON, der Vergleich laeuft als Schatten ohne Wirkung weiter
 * (Betreiber-Entscheid 2 vom 02.10.2026: kein Umgehen je Rechnung, sondern
 * ein Schalter fuer alle, nur Administratoren).
 *
 * Seit Stufe 3c gilt DERSELBE Schalter auch fuer den Rechnungsentwurf
 * (buildProjectServiceLines, entwurfAusJournal.ts): im Journal-Modus baut er
 * aus dem Buchungsjournal, AN baut er wie bis 3b aus dem JSON — ohne Journal,
 * ohne Schatten, ohne Riegel. Kein zweiter Schalter, keine neue Spalte: der
 * Entwurf liest ihn je Aufbau frisch, wie die Ausstellung je Beleg.
 *
 * Gespeichert in der einen Zeile von `public.app_maintenance` (Spalten
 * `tor_json_seit`, `tor_json_von`, Migration Nr. 177): von jedem Angemeldeten
 * lesbar, geschrieben nur ueber die Admin-RPC `maintenance_set_tor_notschalter`.
 *
 * ─── Unbekannt heisst STRENG ───────────────────────────────────────────────
 * Anders als die Bauphase (maintenanceLogic, fail-open) faellt der Schalter
 * bei jedem Zweifel auf den JOURNAL-Modus: Lesefehler, fehlende Zeile,
 * fehlende Spalte (Nr. 177 nicht gefahren), ein Wert, der kein Zeitstempel
 * ist. Ein Lesefehler darf das Tor nicht still oeffnen — dasselbe Argument
 * wie bei Entscheid 3 (ein dauerhafter Rechte- oder Netzfehler schaltete die
 * Gegenpruefung sonst unbemerkt ab). Der JSON-Modus gilt nur, wenn die Zeile
 * es ausdruecklich sagt.
 */

/** Woher das Tor die Zuordnung nimmt. */
export type TorModus = 'journal' | 'json';

export interface TorNotschalterStand {
    /** Was beim Ausstellen gilt. */
    modus: TorModus;
    /** false: Zeile oder Spalte nicht lesbar — dann gilt der Journal-Modus. */
    lesbar: boolean;
    /** Seit wann der Notschalter AN ist (ISO-8601); null = aus. */
    seit: string | null;
    /** Wer ihn eingeschaltet hat (Anzeigename); null = unbekannt oder aus. */
    von: string | null;
}

export const TOR_NOTSCHALTER_UNLESBAR: TorNotschalterStand = Object.freeze({
    modus: 'journal',
    lesbar: false,
    seit: null,
    von: null,
}) as TorNotschalterStand;

/**
 * Der Stand aus einer Zeile von `app_maintenance` oder aus der Antwort der
 * RPC (`{tor_json_seit, tor_json_von}`). Array oder Einzelobjekt.
 *
 * Fehlt das Feld `tor_json_seit` ganz, ist die Spalte unbekannt (Nr. 177
 * nicht gefahren oder eine Antwort anderer Form) — das ist NICHT „aus",
 * sondern „nicht lesbar"; der Modus ist in beiden Faellen Journal, die
 * Anzeige unterscheidet sie.
 */
export const torNotschalterAusZeile = (zeile: unknown): TorNotschalterStand => {
    const z = Array.isArray(zeile) ? zeile[0] : zeile;
    if (!z || typeof z !== 'object') return TOR_NOTSCHALTER_UNLESBAR;
    const r = z as Record<string, unknown>;
    if (!('tor_json_seit' in r)) return TOR_NOTSCHALTER_UNLESBAR;
    const seit = r.tor_json_seit;
    if (seit === null) return { modus: 'journal', lesbar: true, seit: null, von: null };
    if (typeof seit === 'string' && seit.trim() !== '' && Number.isFinite(Date.parse(seit))) {
        const von = typeof r.tor_json_von === 'string' && r.tor_json_von.trim() !== '' ? r.tor_json_von.trim() : null;
        return { modus: 'json', lesbar: true, seit, von };
    }
    // Ein Wert, den hier niemand versteht, oeffnet das Tor nicht.
    return TOR_NOTSCHALTER_UNLESBAR;
};

/** „23.10.2026 14:05" in Berliner Zeit — fuer die Anzeige. */
export const torZeitpunkt = (iso: string | null | undefined): string => {
    const ms = Date.parse(String(iso ?? ''));
    if (!Number.isFinite(ms)) return String(iso ?? '');
    const teile = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(new Date(ms));
    const t = (typ: string) => teile.find((p) => p.type === typ)?.value ?? '';
    return `${t('day')}.${t('month')}.${t('year')} ${t('hour')}:${t('minute')}`;
};

/**
 * Die Zustandszeile der Admin-Karte — Wortlaut im Abschlussbericht. Seit
 * Stufe 3c nennt sie Zuordnung UND Rechnungsentwurf: der eine Schalter legt
 * beide um.
 */
export const torNotschalterText = (stand: TorNotschalterStand): string => {
    if (!stand.lesbar) {
        return 'Stand nicht lesbar (Migration Nr. 177 nicht gefahren oder keine Verbindung). Dann gilt der Journal-Modus: Die Zuordnung beim Ausstellen und der Rechnungsentwurf kommen aus dem Buchungsjournal, das Tor sperrt bei Abweichung.';
    }
    if (stand.modus === 'json') {
        return `Notschalter AN seit ${torZeitpunkt(stand.seit)}${stand.von ? ` (${stand.von})` : ''}: Die Zuordnung beim Ausstellen und der Rechnungsentwurf kommen aus dem Projekt-JSON, das Tor sperrt nicht. Abweichungen werden nur gemeldet.`;
    }
    return 'Notschalter AUS (Normalbetrieb): Die Zuordnung beim Ausstellen und der Rechnungsentwurf kommen aus dem Buchungsjournal. Weicht es mit Geldwirkung vom Projektprotokoll ab oder ist es nicht lesbar, sperrt das Tor die Ausstellung; ist es beim Entwurf nicht lesbar oder fehlt ihm eine eben gebuchte Leistung, entsteht kein Entwurf.';
};

/** Die Rueckfrage im Seiten-Dialog vor dem Umlegen — Wortlaut im Abschlussbericht. */
export const torNotschalterRueckfrage = (an: boolean): string =>
    an
        ? 'Tor-Notschalter EINSCHALTEN? Ab sofort ordnet jede Ausstellung in allen Tabs wieder aus dem Projekt-JSON zu, und jeder Rechnungsentwurf wird wieder aus dem Projekt-JSON aufgebaut; das Tor sperrt bei einer Abweichung zum Buchungsjournal NICHT mehr, Abweichungen werden nur noch gemeldet. Nur bei einer Störung des Buchungsjournals verwenden und danach wieder ausschalten.'
        : 'Tor-Notschalter AUSSCHALTEN? Ab sofort ordnet jede Ausstellung wieder aus dem Buchungsjournal zu, und jeder Rechnungsentwurf wird aus dem Buchungsjournal aufgebaut; das Tor sperrt bei einer Abweichung mit Geldwirkung oder wenn das Journal nicht lesbar ist.';
